import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  CalendarRange,
  ExternalLink,
  FileText,
  Layers,
  Mail,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from '@/components/feedback/toast-store'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { Textarea } from '@/components/ui/Textarea'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { admissionSettingsQuery, useSaveAdmissionSettings } from '@/features/admissions/api'
import type { AdmissionSettings } from '@/features/admissions/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const schema = z
  .object({
    open: z.boolean(),
    academicYearId: z.string(),
    opensOn: z.string(),
    closesOn: z.string(),
    classes: z.array(
      z.object({
        classId: z.number(),
        name: z.string(),
        offered: z.boolean(),
        places: z.string().regex(/^\d*$/, 'Enter a whole number.'),
        description: z.string().max(500),
      }),
    ),
    introText: z.string().max(5000),
    closedMessage: z.string().max(5000),
    confirmationMessage: z.string().max(5000),
    requiredDocuments: z.array(
      z.object({
        label: z.string().trim().min(1, 'Enter the name of the document.').max(150),
        description: z.string().max(500),
        required: z.boolean(),
        upload: z.boolean(),
      }),
    ),
    notifyEmail: z
      .string()
      .trim()
      .refine((v) => v === '' || z.email().safeParse(v).success, 'Enter a valid email address.'),
    askPreviousSchool: z.boolean(),
  })
  .refine((v) => !v.open || v.academicYearId !== '', {
    path: ['academicYearId'],
    message: 'Choose the academic year applicants will join.',
  })
  .refine((v) => v.opensOn === '' || v.closesOn === '' || v.closesOn >= v.opensOn, {
    path: ['closesOn'],
    message: 'The closing date must be on or after the opening date.',
  })
type SettingsForm = z.infer<typeof schema>

const FIELDS = [
  'academicYearId',
  'opensOn',
  'closesOn',
  'classes',
  'introText',
  'closedMessage',
  'confirmationMessage',
  'requiredDocuments',
  'notifyEmail',
]

/** How the school runs online admissions. */
export function AdmissionSettingsPage() {
  const { t } = useTranslation('admissions')
  useDocumentTitle('Admission settings')
  const settings = useQuery(admissionSettingsQuery())
  const classes = useQuery(classesQuery())
  const years = useQuery(yearsQuery())
  const classOptions = useMemo(
    () => (classes.data ?? []).map((c) => ({ id: c.id, name: c.name })),
    [classes.data],
  )
  const yearOptions = useMemo(
    () => (years.data ?? []).map((y) => ({ id: y.id, name: y.name, isCurrent: y.isCurrent })),
    [years.data],
  )

  return (
    <>
      <PageHeader
        title={t('Admission settings')}
        description={t(
          'Open the online form, choose the year and the classes offered with their places, and the documents families provide.',
        )}
        breadcrumbs={[{ label: t('Online admissions'), to: '/admin/admissions' }, { label: t('Settings') }]}
        homeHref="/admin"
        actions={
          settings.data ? (
            <Button variant="secondary" leftIcon={<ExternalLink />} asChild>
              <a href={settings.data.publicUrl} target="_blank" rel="noreferrer">
                {t('Preview the form')}
              </a>
            </Button>
          ) : null
        }
      />
      {settings.isPending || classes.isPending || years.isPending ? (
        <LoadingState />
      ) : settings.error || classes.error || years.error ? (
        <ErrorState
          onRetry={() => void Promise.all([settings.refetch(), classes.refetch(), years.refetch()])}
        />
      ) : (
        <SettingsFormView current={settings.data} classList={classOptions} yearList={yearOptions} />
      )}
    </>
  )
}

function toForm(s: AdmissionSettings, classes: { id: number; name: string }[]): SettingsForm {
  const offered = new Map(s.classes.map((c) => [c.classId, c]))
  // Offered classes first, in their order, then the others.
  const ordered = [
    ...s.classes.map((c) => ({ id: c.classId, name: c.name })),
    ...classes.filter((c) => !offered.has(c.id)),
  ]
  return {
    open: s.open,
    academicYearId: s.academicYear ? String(s.academicYear.id) : '',
    opensOn: s.opensOn ?? '',
    closesOn: s.closesOn ?? '',
    classes: ordered.map((c) => {
      const o = offered.get(c.id)
      return {
        classId: c.id,
        name: c.name,
        offered: Boolean(o),
        places: o?.places != null ? String(o.places) : '',
        description: o?.description ?? '',
      }
    }),
    introText: s.introText ?? '',
    closedMessage: s.closedMessage ?? '',
    confirmationMessage: s.confirmationMessage ?? '',
    requiredDocuments: s.requiredDocuments.map((d) => ({ ...d, description: d.description ?? '' })),
    notifyEmail: s.notifyEmail ?? '',
    askPreviousSchool: s.askPreviousSchool,
  }
}

function SettingsFormView({
  current,
  classList,
  yearList,
}: {
  current: AdmissionSettings
  classList: { id: number; name: string }[]
  yearList: { id: number; name: string; isCurrent: boolean }[]
}) {
  const { t } = useTranslation('admissions')
  const save = useSaveAdmissionSettings()
  const form = useForm<SettingsForm>({
    resolver: zodResolver(schema),
    defaultValues: toForm(current, classList),
  })
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty },
  } = form
  const docs = useFieldArray({ control, name: 'requiredDocuments' })
  const classRows = useFieldArray({ control, name: 'classes', keyName: 'key' })
  useEffect(() => reset(toForm(current, classList)), [current, classList, reset])

  const blank = (v: string) => (v.trim() === '' ? null : v.trim())
  const onSubmit = handleSubmit((v) =>
    save.mutate(
      {
        open: v.open,
        academicYearId: v.academicYearId ? Number(v.academicYearId) : null,
        opensOn: blank(v.opensOn),
        closesOn: blank(v.closesOn),
        classes: v.classes
          .filter((c) => c.offered)
          .map((c) => ({
            classId: c.classId,
            places: c.places === '' ? null : Number(c.places),
            description: blank(c.description),
          })),
        introText: blank(v.introText),
        closedMessage: blank(v.closedMessage),
        confirmationMessage: blank(v.confirmationMessage),
        requiredDocuments: v.requiredDocuments.map((d) => ({
          ...d,
          label: d.label.trim(),
          description: blank(d.description),
        })),
        notifyEmail: blank(v.notifyEmail),
        askPreviousSchool: v.askPreviousSchool,
      },
      {
        onSuccess: () => toast.success(t('Admission settings saved')),
        onError: (e) => applyServerErrors(e, setError, FIELDS),
      },
    ),
  )
  const takenByClass = new Map(current.classes.map((c) => [c.classId, c.taken]))

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-5">
      <Alert tone={current.isOpenNow ? 'success' : 'warning'}>
        {current.isOpenNow
          ? t('The form is open for {{year}}.', { year: current.academicYear?.name ?? '' })
          : t('The form is closed: families cannot apply at the moment.')}
      </Alert>

      <FormSection
        title={t('Opening')}
        icon={<CalendarRange />}
        description={t(
          'The form is open when it is switched on, between the dates, with at least one class offered that has places left.',
        )}
      >
        <div className="flex items-center gap-3 sm:col-span-2">
          <Controller
            name="open"
            control={control}
            render={({ field }) => (
              <Switch id="adm-open" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <label htmlFor="adm-open" className="text-sm font-medium text-ink">
            {t('Accept online applications')}
          </label>
        </div>
        <FormField label={t('Academic year applicants join')} error={errors.academicYearId?.message}>
          <Select
            placeholder={t('Choose…')}
            options={yearList.map((y) => ({
              value: y.id,
              label: y.isCurrent ? t('{{name}} (current)', { name: y.name }) : y.name,
            }))}
            {...register('academicYearId')}
          />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={t('Opens on')} error={errors.opensOn?.message} hint={t('Optional')}>
            <Input type="date" {...register('opensOn')} />
          </FormField>
          <FormField label={t('Closes on')} error={errors.closesOn?.message} hint={t('Optional')}>
            <Input type="date" {...register('closesOn')} />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        title={t('Classes offered')}
        icon={<Layers />}
        columns={1}
        description={t(
          'Leave the places empty for no limit. Accepted and enrolled applications take a place.',
        )}
      >
        <ul className="space-y-3">
          {classRows.fields.map((c, i) => {
            const offered = watch(`classes.${i}.offered`)
            return (
              <li key={c.key} className="rounded-control border border-line p-3">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <Controller
                    name={`classes.${i}.offered`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id={`cls-${c.classId}`}
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                      />
                    )}
                  />
                  <label htmlFor={`cls-${c.classId}`} className="min-w-0 flex-1 font-medium text-ink">
                    {c.name}
                  </label>
                  {offered ? (
                    <span className="text-xs text-muted">
                      {t('{{count}} places taken', { count: takenByClass.get(c.classId) ?? 0 })}
                    </span>
                  ) : null}
                </div>
                {offered ? (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[8rem_1fr]">
                    <FormField label={t('Places')} error={errors.classes?.[i]?.places?.message}>
                      <Input inputMode="numeric" {...register(`classes.${i}.places`)} />
                    </FormField>
                    <FormField
                      label={t('What families should know (série, subjects…)')}
                      error={errors.classes?.[i]?.description?.message}
                    >
                      <Input {...register(`classes.${i}.description`)} />
                    </FormField>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </FormSection>

      <FormSection title={t('Texts for families')} icon={<Mail />} columns={1}>
        <FormField label={t('Introduction')} hint={t('Shown at the top of the form.')}>
          <Textarea rows={3} {...register('introText')} />
        </FormField>
        <FormField label={t('Message when closed')}>
          <Textarea rows={2} {...register('closedMessage')} />
        </FormField>
        <FormField
          label={t('Confirmation message')}
          hint={t('Shown after sending and in the confirmation email.')}
        >
          <Textarea rows={3} {...register('confirmationMessage')} />
        </FormField>
        <FormField
          label={t('Send a copy of each application to')}
          error={errors.notifyEmail?.message}
          hint={t('Optional')}
        >
          <Input type="email" {...register('notifyEmail')} />
        </FormField>
        <div className="flex items-center gap-3">
          <Controller
            name="askPreviousSchool"
            control={control}
            render={({ field }) => (
              <Switch id="adm-prev" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <label htmlFor="adm-prev" className="text-sm text-ink">
            {t('Ask for the previous school')}
          </label>
        </div>
      </FormSection>

      <FormSection
        title={t('Required documents')}
        icon={<FileText />}
        columns={1}
        description={t(
          'Documents sent with the form are uploaded (PDF, JPEG or PNG, 5 MB). The others are listed as documents to bring.',
        )}
      >
        <ul className="space-y-3">
          {docs.fields.map((d, i) => (
            <li key={d.id} className="rounded-control border border-line p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  label={t('Document')}
                  error={errors.requiredDocuments?.[i]?.label?.message}
                  required
                >
                  <Input {...register(`requiredDocuments.${i}.label`)} />
                </FormField>
                <FormField label={t('Details')} hint={t('Optional')}>
                  <Input {...register(`requiredDocuments.${i}.description`)} />
                </FormField>
              </div>
              <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <Controller
                  name={`requiredDocuments.${i}.required`}
                  control={control}
                  render={({ field }) => (
                    <span className="flex items-center gap-2">
                      <Checkbox
                        id={`doc-req-${d.id}`}
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                      />
                      <label htmlFor={`doc-req-${d.id}`}>{t('Required')}</label>
                    </span>
                  )}
                />
                <Controller
                  name={`requiredDocuments.${i}.upload`}
                  control={control}
                  render={({ field }) => (
                    <span className="flex items-center gap-2">
                      <Checkbox
                        id={`doc-up-${d.id}`}
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                      />
                      <label htmlFor={`doc-up-${d.id}`}>{t('Uploaded with the form')}</label>
                    </span>
                  )}
                />
                <span className="ml-auto flex gap-1">
                  <IconButton
                    label={t('Move up')}
                    icon={<ArrowUp />}
                    size="icon-sm"
                    disabled={i === 0}
                    onClick={() => docs.move(i, i - 1)}
                  />
                  <IconButton
                    label={t('Move down')}
                    icon={<ArrowDown />}
                    size="icon-sm"
                    disabled={i === docs.fields.length - 1}
                    onClick={() => docs.move(i, i + 1)}
                  />
                  <IconButton
                    label={t('Remove')}
                    icon={<Trash2 />}
                    size="icon-sm"
                    onClick={() => docs.remove(i)}
                  />
                </span>
              </div>
            </li>
          ))}
        </ul>
        <div>
          <Button
            variant="secondary"
            leftIcon={<Plus />}
            onClick={() => docs.append({ label: '', description: '', required: true, upload: true })}
          >
            {t('Add a document')}
          </Button>
        </div>
      </FormSection>

      <FormActions>
        <Button
          type="submit"
          leftIcon={<Save />}
          loading={save.isPending}
          disabled={!isDirty && !save.isError}
        >
          {t('Save the settings')}
        </Button>
      </FormActions>
    </form>
  )
}
